// Alleen-lezen toegang voor gasten (Tristan).
//
// Drie lagen:
//  1. De database laat deze gebruiker alleen SELECT doen (is_os_lezer(), migratie os_lezer_tristan.sql). Dat is de echte grens.
//  2. Deze module weigert schrijfverzoeken ook in de browser, zodat de schermen een nette melding geven.
//  3. Kopiëren, afdrukken, downloaden en slepen worden geblokkeerd en er staat een watermerk over het scherm.
//
// Een schermafbeelding via het besturingssysteem (Cmd+Shift+4, Knipprogramma, een foto van het scherm) kan
// een website niet tegenhouden. Het watermerk met naam en tijdstip maakt een uitgelekte afbeelding wel herleidbaar.

export const LEZERS = ["tristan_b@hotmail.nl"];

export const isLezer = (email) => LEZERS.includes(String(email || "").toLowerCase());

let actief = false;
export const leesModusActief = () => actief;
export const zetLeesModus = (aan) => { actief = !!aan; };

export const GEBLOKKEERD_EVENT = "os-lees-geblokkeerd";
export const GEBLOKKEERD_TEKST = "Dit is nu niet beschikbaar.";

// `reden` komt in het auditlogboek; `stil` registreert zonder melding op het scherm.
const meld = (reden, stil) => {
  try { window.dispatchEvent(new CustomEvent(GEBLOKKEERD_EVENT, { detail: { tekst: GEBLOKKEERD_TEKST, reden: reden || "onbekend", stil: !!stil } })); } catch (e) { /* stil */ }
};

/**
 * Vervanging voor fetch in de Supabase-client. In leesmodus mag alleen gelezen worden:
 * GET en HEAD, de inlog zelf en database-functies (rpc, die door de database zelf op schrijfrechten worden gecontroleerd).
 */
export function leesModusFetch(input, init) {
  const doorgeven = (typeof fetch === "function" ? fetch : window.fetch).bind(window);
  if (!actief) return doorgeven(input, init);
  const methode = String((init && init.method) || (input && input.method) || "GET").toUpperCase();
  const url = String((input && input.url) || input || "");
  const leest = methode === "GET" || methode === "HEAD";
  const toegestaan = leest || url.includes("/auth/v1/") || url.includes("/rest/v1/rpc/");
  if (toegestaan) return doorgeven(input, init);
  meld("schrijfpoging " + methode + " " + url.replace(/^https?:\/\/[^/]+/, "").split("?")[0]);
  return Promise.resolve(new Response(JSON.stringify({ message: GEBLOKKEERD_TEKST, code: "LEES_MODUS" }), {
    status: 403,
    headers: { "Content-Type": "application/json" },
  }));
}

const GEBLOKKEERDE_TOETSEN = [
  // [toets, ctrl/cmd verplicht, shift verplicht]
  ["p", true, false], // afdrukken
  ["s", true, false], // opslaan
  ["c", true, false], // kopiëren
  ["x", true, false], // knippen
  ["a", true, false], // alles selecteren
  ["u", true, false], // bron bekijken
  ["3", true, true], ["4", true, true], ["5", true, true], // schermafbeelding op macOS
];

/**
 * Zet de beveiliging aan. Geeft een functie terug die alles weer terugzet.
 * `bijStatus(verborgen)` wordt aangeroepen als het venster uit beeld raakt (inhoud verbergen).
 */
export function activeerLeesBeveiliging(bijStatus) {
  const opruimen = [];
  const luister = (doel, naam, fn, opts) => { doel.addEventListener(naam, fn, opts); opruimen.push(() => doel.removeEventListener(naam, fn, opts)); };
  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

  // Kopiëren, knippen, plakken, slepen, rechtermuisknop, selecteren.
  ["copy", "cut", "dragstart", "contextmenu", "selectstart"].forEach((naam) => {
    luister(document, naam, (e) => {
      const el = e.target && e.target.nodeType === 1 ? e.target : e.target && e.target.parentElement;
      // In invoervelden blijft selecteren werken (zoeken), kopiëren niet.
      if (naam === "selectstart" && el && el.closest && el.closest("input, textarea")) return;
      stop(e);
      if (naam === "copy" || naam === "cut") {
        try { e.clipboardData && e.clipboardData.setData("text/plain", ""); } catch (x) { /* stil */ }
        meld(naam === "copy" ? "kopieerpoging" : "knippoging");
      } else if (naam === "dragstart") meld("sleeppoging", true);
    }, true);
  });

  // Toetsen: afdrukken, opslaan, kopiëren, schermafbeelding. PrintScreen kan niet voorkomen worden, wel wissen we het klembord.
  luister(window, "keydown", (e) => {
    const k = String(e.key || "").toLowerCase();
    const cmd = e.ctrlKey || e.metaKey;
    if (GEBLOKKEERDE_TOETSEN.some(([t, c, s]) => t === k && (!c || cmd) && (!s || e.shiftKey))) {
      stop(e);
      meld("toets " + (cmd ? "cmd/ctrl+" : "") + (e.shiftKey ? "shift+" : "") + k, true);
    }
  }, true);
  luister(window, "keyup", (e) => {
    if (e.key === "PrintScreen") {
      meld("PrintScreen", true);
      try { navigator.clipboard && navigator.clipboard.writeText(" "); } catch (x) { /* stil */ }
      if (bijStatus) { bijStatus(true); setTimeout(() => bijStatus(false), 1500); }
    }
  }, true);

  // Afdrukken en opslaan als pdf: de pagina is dan leeg (zie ook de stijlregel in LeesModusLaag.jsx).
  luister(window, "beforeprint", () => { meld("afdrukpoging (browsermenu)", true); bijStatus && bijStatus(true); });
  luister(window, "afterprint", () => { bijStatus && bijStatus(false); });

  // Inhoud verbergen zodra het venster niet meer voorop staat (schermopname- en deelprogramma's, andere vensters).
  const zichtbaar = () => bijStatus && bijStatus(document.visibilityState !== "visible");
  luister(document, "visibilitychange", zichtbaar);
  // Op een telefoon of tablet vuurt blur ook bij een melding, het toetsenbord of het notificatiescherm; dan bleef de
  // inhoud donker en leek het scherm vast te zitten. Daar vertrouwen we op visibilitychange (app naar de achtergrond).
  const aanraking = typeof navigator !== "undefined" && (navigator.maxTouchPoints || 0) > 0;
  if (!aanraking) {
    luister(window, "blur", () => bijStatus && bijStatus(true));
    luister(window, "focus", () => bijStatus && bijStatus(false));
  }

  // Programmatisch kopiëren, downloaden en afdrukken (de OS doet dat op tientallen plekken).
  const origClip = navigator.clipboard && navigator.clipboard.writeText;
  if (origClip) {
    try { navigator.clipboard.writeText = () => { meld("klembord schrijven"); return Promise.reject(new Error(GEBLOKKEERD_TEKST)); }; opruimen.push(() => { navigator.clipboard.writeText = origClip; }); } catch (e) { /* stil */ }
  }
  const origKlik = HTMLAnchorElement.prototype.click;
  HTMLAnchorElement.prototype.click = function () {
    if (this.hasAttribute("download")) { meld("downloadpoging " + String(this.getAttribute("download")).slice(0, 80)); return undefined; }
    return origKlik.apply(this, arguments);
  };
  opruimen.push(() => { HTMLAnchorElement.prototype.click = origKlik; });
  const origPrint = window.print;
  window.print = () => { meld("afdrukpoging"); };
  opruimen.push(() => { window.print = origPrint; });

  return () => opruimen.forEach((f) => f());
}
